// ============================================================================
// Canonical Cattle Batches index route contract
// ============================================================================
//
// `/cattle/batches/*` is a SPLAT row, and it is now owned by three separate
// surfaces: this hub, the persisted record page, and the forecast projection.
// That splat is exactly why the index needs its own narrow predicate rather
// than leaning on the generic route classifier.
//
// `routedLocationFor` canonicalizes ANY unowned pathname back to the row's
// base during a view-led transition frame, so a `cattlebatches` view paired
// with a stale browser path resolves to `/cattle/batches` and therefore to the
// index surface. Without this predicate the resolver would answer RELEASED for
// a route the browser is not actually on. The former
// `claimLegacyAllDataBoundary` path would have returned null; the current
// disposition instead terminal-recovers rather than minting a mismatched claim.
//
// The trailing-slash form is deliberately excluded. `/cattle/batches/` is
// owned by the splat, so it survives `routedLocationFor` unchanged and reaches
// `CattleBatchesHub` through the historical router; keeping it outside the
// released capability preserves explicit recovery instead of quietly widening
// the audited capability.
//
// Search and hash ARE accepted. CattleBatchesHub reads neither: its toolbar
// filters, sort rule, and saved-view selection are component and persisted
// local state, never URL state. React Router already carries both separately
// from pathname, so admitting them adds no second direct-entry shape.
// ============================================================================

export const CATTLE_BATCHES_INDEX_PATHNAME = '/cattle/batches';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

/**
 * The one route rendered by CattleBatchesRouter as `cattle.batches.index`.
 * Exact equality is the whole contract: any non-empty suffix belongs to a
 * sibling release or to explicit route recovery, and the empty-suffix trailing
 * slash also receives recovery rather than a released claim.
 */
export function isCanonicalCattleBatchesIndexPathname(pathname: unknown): pathname is string {
  return pathname === CATTLE_BATCHES_INDEX_PATHNAME;
}

/**
 * Prove the raw browser location before the resolver consults the ledger. A
 * bare string must be exactly a pathname; only a real location object may
 * carry the independently harmless search/hash presentation state, and only
 * when both are genuine strings.
 */
export function isCanonicalCattleBatchesIndexLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalCattleBatchesIndexPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    isCanonicalCattleBatchesIndexPathname(pathname) &&
    (search === undefined || typeof search === 'string') &&
    (hash === undefined || typeof hash === 'string')
  );
}
